import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, Table, IconButton, Button } from '@mui/joy';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { canEditCategory } from '../../utils/permissions';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authFetch } from '../../utils/authFetch';

export default function Dashboard() {
  const navigate = useNavigate();

  const selectedOrg = localStorage.getItem('selectedOrg');
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);
  const perPage = 10;

  useEffect(() => {
    if (!selectedOrg) return;

    const fetchCategories = async () => {
      try {
        const res = await authFetch(
          `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/category`,
          { credentials: 'include' }
        );

        if (!res.ok) throw new Error('Errore nel fetch delle categorie');

        const data = await res.json();

        if (Array.isArray(data)) {
          setCategories(data);
        } else {
          console.warn('Formato categoria non valido:', data);
          toast.error('Risposta inattesa dal server categorie', {
            position: 'top-right',
            autoClose: 3000,
          });
        }
      } catch (err) {
        console.error('Errore fetch categorie:', err);
        toast.error('Errore nel caricamento delle categorie', {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    };

    fetchCategories();
  }, [selectedOrg]);

  const paginatedCategories = useMemo(() => {
    const start = (page - 1) * perPage;
    return categories.slice(start, start + perPage);
  }, [categories, page]);

  const totalPages = Math.ceil(categories.length / perPage);

  const handleCreateCategory = async () => {
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/category`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }
      );
  
      if (!res.ok) throw new Error('Errore nella creazione della categoria');
  
      const data = await res.json();

      if (data && data.id) {
        navigate(`/categorie/${data.id}`);
      } else {
        toast.error('Errore: risposta inattesa dal server');
      }      
      
    } catch (err) {
      console.error(err);
      toast.error('Errore durante la creazione della categoria', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };  

  return (

        <Box>
          <Typography level="h3" mb={2}>
            Categorie
          </Typography>

          <Box mb={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box>
              <Typography level="body-md" color="neutral">
                Ricorda: puoi modificare ogni categoria e adattarla ai tuoi mezzi, senza doverne creare una nuova ogni volta.
              </Typography>
            </Box>
            <Box sx={{ marginLeft: 'auto' }}>
              {canEditCategory() && (
                <Button
                  variant="solid"
                  color="primary"
                  onClick={handleCreateCategory}
                >
                  + Aggiungi categoria
                </Button>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              borderRadius: 'lg',
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'neutral.outlinedBorder',
            }}
          >
            <Table
              size="sm"
              sx={{
                tableLayout: 'auto',
                '& thead th': {
                  bgcolor: 'neutral.softBg',
                  textAlign: 'left',
                  px: 2,
                  py: 1,
                  fontSize: 'sm',
                  whiteSpace: 'nowrap',
                },
                '& tbody td': {
                  px: 2,
                  py: 1,
                  fontSize: 'md',
                  lineHeight: 1.4,
                },
                '& tbody tr:hover': {
                  bgcolor: 'neutral.plainHoverBg',
                  cursor: 'pointer',
                },
              }}
            >
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Descrizione</th>
                  <th>Veicoli</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {paginatedCategories.length > 0 ? (
                  paginatedCategories.map((cat) => (
                    <tr
                      key={cat.id}
                      onClick={() => navigate(`/categorie/${cat.id}`)}
                    >
                      <td>{cat.name}</td>
                      <td>{cat.description || '—'}</td>
                      <td>{cat.vehicle_count || '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <IconButton variant="soft" size="sm">
                          <ChevronRight size={18} />
                        </IconButton>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: '12px' }}>
                      Nessuna categoria trovata.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Box>

          {totalPages > 1 && (
            <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
              <Button
                size="sm"
                variant="outlined"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                startDecorator={<ChevronLeft size={14} />}
              >
                Precedente
              </Button>

              <Typography level="body-sm">
                Pagina {page} di {totalPages}
              </Typography>

              <Button
                size="sm"
                variant="outlined"
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
                endDecorator={<ChevronRight size={14} />}
              >
                Successiva
              </Button>
            </Box>
          )}
        </Box>
  );
}
